import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import CardStateHelper, { getCardsState } from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { BoosterType } from '../BoosterType';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { isNearEndGame } from '../../Level/LevelStateHelper';
import { TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import CardStateModifierHelper from '../CardStateModifierHelper';
import { STREAK_PROGRESS_STEPS } from '../../../Constants/Level';
import { ICardModifierData } from 'Prototype/Level/Card/ICardModifierData';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { CardState } from 'Prototype/Level/Card/CardState';

export default class UndoBoosterBehavior extends BaseBoosterBehavior {
    public play(state: IGameState, meta?: IBaseBoosterBehaviorPlayMeta): IGameState {
        let cardsState = {
            ...state.levelState.cardsState,
        };

        const lastMove = cardsState.undoHistory[cardsState.undoHistory.length - 1];
        let resetStreak = false;

        // Move card back to in-play.
        if (lastMove.cardState === CardState.IN_PLAY) {
            cardsState = CardStateHelper.setCardInPlay(cardsState, lastMove.id);

            // Task for animation.
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.UNDO_MATCH,
                cards: [lastMove.id],
            });

            resetStreak = cardsState.matchSequence.length % STREAK_PROGRESS_STEPS === 0;
        }

        // Move card back to stack.
        if (lastMove.cardState === CardState.IN_STACK) {
            cardsState = CardStateHelper.setCardInStack(cardsState, lastMove.id);

            // Task for animation.
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.UNDO_FOLD,
                cards: [lastMove.id],
            });
        }

        cardsState = {
            ...cardsState,
            matchSequence: resetStreak ? [] : lastMove.matchSequence,
            streakScore: resetStreak ? 0 : lastMove.streakScore,

            // Revert card to previous state by ID.
            cards: {
                ...cardsState.cards,
                [lastMove.id]: { ...lastMove },
            },

            // Slice of new array with last element removed.
            undoHistory: cardsState.undoHistory.slice(0, cardsState.undoHistory.length - 1),
        };

        state = CardStateHelper.updateGameState(state, cardsState);

        const allCards = CardStateHelper.getAllCardIds(state);

        allCards.forEach(cardID => {
            CardStateHelper.getModifiersOfType(state.levelState.cardsState, cardID).forEach(
                (modifier: ICardModifierData) => {
                    const modBehavior = CardStateModifierHelper.getModifierBehavior(
                        state.levelState.cardsState,
                        modifier,
                        cardID
                    );

                    const prevCardState = lastMove.cards[cardID];
                    const prevModifier = prevCardState?.modifiers.find(
                        (m: ICardModifierData) => m.type === modifier.type
                    );

                    state = modBehavior.onUndoUsed(state, prevCardState, prevModifier);
                    cardsState = getCardsState(state);
                }
            );
        });

        const undoFlipCards: string[] = [];

        cardsState.inPlay.forEach((cardID, index) => {
            const prevCardState = lastMove.cards[cardID];
            const currentCardState = cardsState.cards[cardID];

            if (currentCardState.faceUp && !prevCardState.faceUp) {
                cardsState = CardStateHelper.updateCard(cardsState, cardID, {
                    faceUp: false,
                });
                undoFlipCards.push(cardID);
            }
        });

        if (undoFlipCards.length > 0) {
            cardsState = CardStateHelper.setStateTaskData(cardsState, {
                task: CardTask.UNDO_FLIP,
                cards: undoFlipCards,
            });
        }

        state = CardStateHelper.updateGameState(state, cardsState);

        return this.consumeItemOrCoins(state);
    }

    public isPlayable(state: IGameState): boolean {
        return state.levelState.cardsState.undoHistory.length > 0 && super.isPlayable(state);
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[BoosterType.UNDO];
    }

    protected getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[BoosterType.UNDO];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);

        // TODO: use InventoryStateHelper.consumeInventory instead
        state = {
            ...state,
            playerState: InventoryStateHelper.setBoosterUndo(
                state.playerState,
                state.playerState.inventory[BoosterType.UNDO] - 1
            ),
        };

        return state;
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tUndoCoins');
        track('tUndoReceived', 1, TrackMethod.ADD);
        state = super.spendCoins(state);
        track('tUndoUsed', 1, TrackMethod.ADD);
        state = {
            ...state,
            levelState: {
                ...state.levelState,
                boosterUses: {
                    ...state.levelState.boosterUses,
                    [BoosterType.UNDO]: state.levelState.boosterUses[BoosterType.UNDO] + 1,
                },
            },
        };

        return state;
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        track('mUndoUsed', 1, TrackMethod.ADD);

        if (isNearEndGame(state)) {
            track('mNumBoostersUsedLossAversion', 1, TrackMethod.ADD);
        }
        return super.consumeItemOrCoins(state);
    }
}
